import { useState, useMemo, useCallback } from "react";
import axios from "axios";
import {
  Download,
  Loader2,
  ChevronDown,
  ChevronRight,
  RotateCcw,
  AlertTriangle,
  Clock,
  Users,
  DollarSign,
  AlertCircle,
  Calendar,
  Eye,
  EyeOff,
  Search,
  RefreshCw,
  Timer,
} from "lucide-react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Badge } from "./components/ui/badge";
import { toast } from "sonner";

import { API } from "./apiConfig";

export function ShiftReview({ data, storeTips, onReset, settings, onUpdateShifts, onDownload, canAccessPremium, breakThreshold, isSampleData }) {
  const [shifts, setShifts] = useState(() => {
    return (data.shifts || []).map((s) => ({
      ...s,
      original_hours: s.original_hours ?? s.elapsed_hours,
      ot_overridden: s.ot_overridden ?? false,
    }));
  });
  const [generating, setGenerating] = useState(false);
  const [reprocessing, setReprocessing] = useState(false);
  const [downloadReady, setDownloadReady] = useState(null);
  const [expandedEmployees, setExpandedEmployees] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState("");
  const [showExcluded, setShowExcluded] = useState(true);
  const [tipDaysOverride, setTipDaysOverride] = useState({});

  const newTipEmployees = useMemo(() => new Set(data.new_tip_employees || []), [data.new_tip_employees]);
  const sessionAlerts = useMemo(() => (data.anomalies || []).filter((a) => a.type === "continued_session"), [data.anomalies]);

  const grouped = useMemo(() => {
    const groups = {};
    shifts.forEach((s) => {
      if (!groups[s.emp_name]) groups[s.emp_name] = [];
      groups[s.emp_name].push(s);
    });
    return Object.entries(groups)
      .sort(([a], [b]) => a.localeCompare(b))
      .filter(([name]) => name.toLowerCase().includes(searchTerm.toLowerCase()));
  }, [shifts, searchTerm]);

  const stats = useMemo(() => {
    const included = shifts.filter((s) => s.included);
    return {
      totalShifts: included.length,
      employees: new Set(included.map((s) => s.emp_name)).size,
      anomalies: included.filter((s) => s.is_anomaly).length,
      breakViolations: included.filter((s) => s.is_break_violation).length,
      shortBreaks: included.filter((s) => s.is_short_break).length,
      weeklyOt: new Set(included.filter((s) => s.is_weekly_ot).map((s) => s.emp_name)).size,
      dailyOt: included.filter((s) => s.overtime != null && s.overtime > 0 && !s.is_weekly_ot).length,
      sickShifts: included.filter((s) => s.is_sick).length,
      excluded: shifts.filter((s) => !s.included).length,
    };
  }, [shifts]);

  const updateShift = useCallback((id, field, value) => {
    setShifts((prev) =>
      prev.map((s) => {
        if (s.id !== id) return s;
        const updated = { ...s, [field]: value };
        if (field === "overtime") updated.ot_overridden = true;
        return updated;
      })
    );
    setDownloadReady(null);
  }, []);

  const toggleEmployee = (name) => {
    setExpandedEmployees((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next;
    });
  };

  const handleReprocess = async () => {
    setReprocessing(true);
    try {
      const response = await axios.post(`${API}/reprocess-shifts`, {
        shifts, store_tips: parseFloat(storeTips),
      });
      const newShifts = response.data.shifts;
      setShifts(newShifts);
      if (onUpdateShifts) onUpdateShifts(newShifts);
      setDownloadReady(null);
      toast.success("Reprocessed!", { description: "OT, flags recalculated." });
    } catch (err) {
      toast.error("Reprocess failed", { description: err.response?.data?.detail || "Error" });
    } finally {
      setReprocessing(false);
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const response = await axios.post(`${API}/generate-excel`, {
        shifts, store_tips: parseFloat(storeTips),
        date_range: data.date_range,
        tip_days_override: Object.keys(tipDaysOverride).length > 0 ? tipDaysOverride : undefined,
        is_sample: isSampleData || false,
      }, { withCredentials: true });
      setDownloadReady(response.data);
      toast.success("Excel generated!", { description: response.data.file_name });
    } catch (err) {
      toast.error("Generation failed", { description: err.response?.data?.detail || "Error" });
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = () => {
    if (!downloadReady?.file_id) return;
    // Always download the freshly generated file (with all user edits)
    const link = document.createElement("a");
    link.href = `${API}/download/${downloadReady.file_id}`;
    link.download = downloadReady.file_name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("Download started", { description: downloadReady.file_name });
  };

  const updateTipDays = useCallback((empName, value) => {
    setTipDaysOverride((prev) => ({ ...prev, [empName]: value }));
    setDownloadReady(null);
  }, []);

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500" data-testid="review-section">
      {/* Review Header */}
      <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
        <div>
          <h2 className="font-heading text-xl font-bold text-[#0A1F3B]">Edit & Review Shifts</h2>
          <p className="text-xs text-slate-600 mt-0.5">{data.file_name} &mdash; {data.date_range}</p>
        </div>
        <div className="flex gap-2">
          <Button
            data-testid="reprocess-btn"
            variant="outline"
            onClick={handleReprocess}
            disabled={reprocessing}
            className="border-slate-200 text-slate-600 hover:text-[#0A1F3B] hover:bg-slate-200 gap-1.5 text-xs"
          >
            {reprocessing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            Reprocess
          </Button>
          {downloadReady ? (
            <Button
              data-testid="download-reviewed-btn"
              onClick={handleDownload}
              className="bg-[#0A1F3B] text-white font-bold gap-1.5 text-xs hover:-translate-y-0.5 hover:shadow-[0_8px_20px_-4px_rgba(10,31,59,0.3)] transition-all"
            >
              <Download size={14} /> Download Excel
            </Button>
          ) : (
            <Button
              data-testid="generate-excel-btn"
              onClick={handleGenerate}
              disabled={generating}
              className="bg-[#0A1F3B] text-white font-bold gap-1.5 text-xs hover:-translate-y-0.5 hover:shadow-[0_8px_20px_-4px_rgba(10,31,59,0.3)] transition-all disabled:opacity-50"
            >
              {generating ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
              Generate Excel
            </Button>
          )}
          <Button
            data-testid="back-to-upload-btn"
            variant="outline"
            onClick={onReset}
            className="border-slate-200 text-slate-600 hover:text-[#0A1F3B] hover:bg-slate-200 gap-1.5 text-xs"
          >
            <RotateCcw size={14} /> New Upload
          </Button>
        </div>
      </div>

      {/* Alerts */}
      {sessionAlerts.length > 0 && (
        <div className="space-y-2 mb-3" data-testid="session-alerts">
          {sessionAlerts.map((alert, i) => (
            <div key={i} className="flex items-start gap-2.5 p-3 bg-amber-50 border border-amber-300 rounded-lg text-xs text-amber-800 leading-relaxed">
              <AlertTriangle size={14} className="text-yellow-700 flex-shrink-0 mt-0.5" />
              <span><strong>{alert.employee}</strong>: {alert.description}</span>
            </div>
          ))}
        </div>
      )}

      {newTipEmployees.size > 0 && (
        <div className="flex items-start gap-2.5 p-3 bg-sky-50 border border-sky-200 rounded-lg text-xs text-sky-800 leading-relaxed mb-3" data-testid="new-emp-alert">
          <Users size={14} className="text-sky-600 flex-shrink-0 mt-0.5" />
          <span><strong>{newTipEmployees.size} new employee{newTipEmployees.size > 1 ? "s" : ""}</strong> added to Tips: {[...newTipEmployees].join(", ")}</span>
        </div>
      )}

      {/* Mini Stats */}
      <div className="flex flex-wrap gap-2 mb-3 p-3 bg-white border border-slate-200 rounded-lg" data-testid="review-stats-bar">
        <MiniStat icon={<Users size={12} />} label="Employees" value={stats.employees} />
        <MiniStat icon={<Clock size={12} />} label="Shifts" value={stats.totalShifts} />
        <MiniStat icon={<AlertTriangle size={12} />} label="Anomalies" value={stats.anomalies} variant={stats.anomalies > 0 ? "warning" : ""} />
        <MiniStat icon={<AlertCircle size={12} />} label={breakThreshold ? `Break >${breakThreshold}h` : "Break"} value={stats.breakViolations} variant={stats.breakViolations > 0 ? "warning" : ""} />
        <MiniStat icon={<Clock size={12} />} label="<30min" value={stats.shortBreaks} variant={stats.shortBreaks > 0 ? "danger" : ""} />
        <MiniStat icon={<Timer size={12} />} label="Daily OT" value={stats.dailyOt} variant={stats.dailyOt > 0 ? "warning" : ""} />
        <MiniStat icon={<Calendar size={12} />} label="Weekly OT" value={`${stats.weeklyOt}`} variant={stats.weeklyOt > 0 ? "purple" : ""} />
        <MiniStat icon={<Clock size={12} />} label="Sick" value={stats.sickShifts} variant={stats.sickShifts > 0 ? "sick" : ""} />
        <MiniStat icon={<DollarSign size={12} />} label="Tips" value={`$${storeTips || 0}`} />
        {stats.excluded > 0 && <MiniStat icon={<EyeOff size={12} />} label="Excluded" value={stats.excluded} variant="muted" />}
      </div>

      {/* Controls */}
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="relative flex-1 max-w-xs">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
          <Input
            data-testid="employee-search"
            placeholder="Search employees..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-8 h-8 text-xs bg-white border-slate-200 text-[#0A1F3B] placeholder-slate-400 focus:border-[#0A1F3B]"
          />
        </div>
        <div className="flex gap-1">
          <Button variant="ghost" size="sm" onClick={() => setExpandedEmployees(new Set(grouped.map(([n]) => n)))} className="text-slate-600 hover:text-[#0A1F3B] hover:bg-slate-200 text-xs h-8" data-testid="expand-all-btn">Expand All</Button>
          <Button variant="ghost" size="sm" onClick={() => setExpandedEmployees(new Set())} className="text-slate-600 hover:text-[#0A1F3B] hover:bg-slate-200 text-xs h-8" data-testid="collapse-all-btn">Collapse</Button>
          <Button variant="ghost" size="sm" onClick={() => setShowExcluded(!showExcluded)} className="text-slate-600 hover:text-[#0A1F3B] hover:bg-slate-200 text-xs h-8" data-testid="toggle-excluded-btn">
            {showExcluded ? <Eye size={12} /> : <EyeOff size={12} />}
            <span className="ml-1">{showExcluded ? "Hide" : "Show"} Excl.</span>
          </Button>
        </div>
      </div>

      {/* Employee Groups */}
      <div className="space-y-1.5 mb-4" data-testid="review-table">
        {grouped.map(([empName, empShifts]) => (
          <EmployeeGroup
            key={empName}
            empName={empName}
            empShifts={empShifts}
            isExpanded={expandedEmployees.has(empName)}
            showExcluded={showExcluded}
            onToggle={toggleEmployee}
            onUpdateShift={updateShift}
            tipDays={tipDaysOverride[empName]}
            onUpdateTipDays={updateTipDays}
            isNew={newTipEmployees.has(empName)}
            breakThreshold={breakThreshold}
          />
        ))}
      </div>

      {/* Legend */}
      <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="review-color-legend">
        <h3 className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-3">Color Legend</h3>
        <div className="flex flex-wrap gap-4">
          {[
            { color: "bg-orange-500", label: breakThreshold ? `Break >${breakThreshold}h` : "Break" },
            { color: "bg-yellow-400", label: "Corrected" },
            { color: "bg-red-400", label: "Short Break" },
            { color: "bg-purple-400", label: "Weekly OT" },
            { color: "bg-blue-400", label: "Daily OT" },
            { color: "bg-sky-400", label: "Sick Hours" },
            { color: "bg-green-400", label: "Fixed Emp" },
            { color: "bg-cyan-400", label: "New (auto-tipped)" },
          ].map((item, i) => (
            <div key={i} className="flex items-center gap-2 text-xs text-slate-600">
              <span className={`w-3 h-3 rounded-sm ${item.color}`} />
              {item.label}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function EmployeeGroup({ empName, empShifts, isExpanded, showExcluded, onToggle, onUpdateShift, tipDays, onUpdateTipDays, isNew, breakThreshold }) {
  const visibleShifts = showExcluded ? empShifts : empShifts.filter((s) => s.included);
  const included = empShifts.filter((s) => s.included);
  const originalTotal = included.reduce((sum, s) => sum + (s.original_hours ?? s.elapsed_hours), 0);
  const currentTotal = included.reduce((sum, s) => sum + s.elapsed_hours, 0);
  const hasCorrectedTotal = Math.abs(currentTotal - originalTotal) > 0.001;
  const totalOt = included.filter((s) => s.overtime != null && s.overtime > 0).reduce((sum, s) => sum + s.overtime, 0);
  const totalSick = included.filter((s) => s.is_sick).reduce((sum, s) => sum + s.elapsed_hours, 0);
  const displayTotal = hasCorrectedTotal ? currentTotal : originalTotal;
  const regularHours = Math.max(0, displayTotal - totalOt);
  const uniqueDates = new Set(included.map((s) => s.clock_in_date));
  const displayTipDays = tipDays !== undefined ? tipDays : uniqueDates.size;

  return (
    <div className={`bg-white border rounded-lg ${isNew ? "border-cyan-500/30" : "border-slate-200"}`} data-testid={`emp-group-${empName.replace(/\s/g, '-')}`}>
      <div className="flex items-center justify-between px-3 py-2 cursor-pointer hover:bg-slate-100 transition-colors overflow-x-auto" onClick={() => onToggle(empName)}>
        <div className="flex items-center gap-2 flex-wrap min-w-0">
          {isExpanded ? <ChevronDown size={14} className="text-slate-600 flex-shrink-0" /> : <ChevronRight size={14} className="text-slate-600 flex-shrink-0" />}
          <span className="text-sm font-semibold text-[#0A1F3B] whitespace-nowrap">{empName}</span>
          {isNew && <span className="text-[11px] font-bold uppercase bg-cyan-500 text-[#0A1F3B] px-1.5 py-0.5 rounded" data-testid={`new-badge-${empName.replace(/\s/g, '-')}`}>NEW</span>}
          <span className="text-[12px] text-slate-500">{visibleShifts.length} shifts</span>
          <span className="text-[12px] font-mono text-slate-600 bg-slate-50 px-2 py-0.5 rounded">{originalTotal.toFixed(2)}h</span>
          {hasCorrectedTotal && <span className="text-[12px] font-mono text-green-700 bg-green-500/10 px-2 py-0.5 rounded" data-testid={`corrected-total-${empName.replace(/\s/g, '-')}`}>Corr: {currentTotal.toFixed(2)}h</span>}
          {totalOt > 0 && <span className="text-[12px] font-mono text-orange-700 bg-orange-500/10 px-2 py-0.5 rounded">{totalOt.toFixed(2)}h OT</span>}
          {totalSick > 0 && <span className="text-[12px] font-mono text-blue-700 bg-blue-500/10 px-2 py-0.5 rounded" data-testid={`sick-hours-${empName.replace(/\s/g, '-')}`}>{totalSick.toFixed(2)}h Sick</span>}
          <span className="text-[12px] font-mono text-blue-700 bg-blue-50 px-2 py-0.5 rounded" data-testid={`regular-hours-${empName.replace(/\s/g, '-')}`}>Reg: {regularHours.toFixed(2)}h</span>
          <span className="inline-flex items-center gap-1 text-[12px] text-slate-600 bg-slate-50 px-2 py-0.5 rounded" onClick={(e) => e.stopPropagation()} data-testid={`tip-days-${empName.replace(/\s/g, '-')}`}>
            Tip Days:
            <input
              type="number"
              min="0"
              value={displayTipDays}
              onChange={(e) => onUpdateTipDays(empName, parseInt(e.target.value) || 0)}
              className="w-8 bg-transparent border border-slate-200 rounded text-center text-[12px] font-mono text-[#0A1F3B] h-5 focus:border-[#0A1F3B] focus:outline-none"
              data-testid={`tip-days-input-${empName.replace(/\s/g, '-')}`}
            />
          </span>
        </div>
        <div className="flex gap-1">
          {getEmployeeFlags(empShifts, breakThreshold).map((f, i) => (
            <Badge key={i} variant={f.variant} className={`text-[11px] px-1.5 py-0 h-4 ${f.className}`}>{f.label}</Badge>
          ))}
        </div>
      </div>

      {isExpanded && (
        <div className="border-t border-slate-100 overflow-x-auto">
          <div className="min-w-[700px]">
            <div className="grid grid-cols-[36px_90px_90px_90px_72px_56px_40px_60px_1fr] gap-0 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-slate-500 bg-slate-50">
              <div>Inc.</div><div>Date</div><div>Clock In</div><div>Clock Out</div><div>Hours</div><div>OT</div><div>Sick</div><div>Flags</div><div>Notes</div>
            </div>
            {visibleShifts.map((s) => <ShiftRow key={s.id} shift={s} onUpdate={onUpdateShift} breakThreshold={breakThreshold} />)}
          </div>
        </div>
      )}
    </div>
  );
}

function ShiftRow({ shift, onUpdate, breakThreshold }) {
  const s = shift;
  const bgClass = !s.included ? "opacity-40" : s.is_sick ? "bg-blue-500/5" : s.is_weekly_ot ? "bg-purple-500/5" : s.is_corrected ? "bg-yellow-500/5" : s.is_anomaly ? "bg-yellow-500/5" : "";

  return (
    <div className={`grid grid-cols-[36px_90px_90px_90px_72px_56px_40px_60px_1fr] gap-0 px-3 py-1 items-center text-xs border-t border-slate-100 hover:bg-slate-50 transition-colors min-w-[700px] ${bgClass}`} data-testid={`shift-row-${s.id}`}>
      <div>
        <input type="checkbox" checked={s.included} onChange={(e) => onUpdate(s.id, "included", e.target.checked)} className="w-3.5 h-3.5 accent-[#0A1F3B] cursor-pointer" data-testid={`shift-include-${s.id}`} />
      </div>
      <div className="font-mono text-[12px] text-slate-600">{s.clock_in_date}</div>
      <div>
        <input type="text" value={s.clock_in_time} onChange={(e) => onUpdate(s.id, "clock_in_time", e.target.value)} className={`w-full font-mono text-[12px] bg-white border border-transparent rounded px-1 py-0.5 text-slate-900 focus:border-[#0A1F3B] focus:outline-none hover:border-slate-200 ${s.is_short_break ? "border-red-500/30 bg-red-50" : ""}`} data-testid={`shift-cin-${s.id}`} />
      </div>
      <div>
        <input type="text" value={s.clock_out_time} onChange={(e) => onUpdate(s.id, "clock_out_time", e.target.value)} className={`w-full font-mono text-[12px] bg-white border border-transparent rounded px-1 py-0.5 text-slate-900 focus:border-[#0A1F3B] focus:outline-none hover:border-slate-200 ${s.is_short_break ? "border-red-500/30 bg-red-50" : ""}`} data-testid={`shift-cout-${s.id}`} />
      </div>
      <div>
        <input type="number" step="0.01" value={s.elapsed_hours} onChange={(e) => onUpdate(s.id, "elapsed_hours", parseFloat(e.target.value) || 0)} className={`w-full font-mono text-[12px] font-semibold bg-transparent border border-transparent rounded px-1 py-0.5 text-slate-900 focus:border-[#0A1F3B] focus:outline-none hover:border-slate-200 ${s.is_break_violation ? "border-orange-500/30 bg-orange-500/5" : ""}`} data-testid={`shift-hours-${s.id}`} />
      </div>
      <div>
        <input type="number" step="0.01" value={s.overtime != null ? s.overtime : ""} onChange={(e) => { const val = e.target.value; onUpdate(s.id, "overtime", val === "" ? null : parseFloat(val) || 0); }} className={`w-full font-mono text-[12px] font-semibold text-orange-700 bg-transparent border border-transparent rounded px-1 py-0.5 text-center focus:border-[#0A1F3B] focus:outline-none hover:border-slate-200 ${s.ot_overridden ? "border-yellow-500/30 bg-yellow-500/5" : ""}`} placeholder="-" data-testid={`shift-ot-${s.id}`} />
      </div>
      <div className="flex items-center justify-center">
        <input type="checkbox" checked={s.is_sick || false} onChange={(e) => onUpdate(s.id, "is_sick", e.target.checked)} className="w-3.5 h-3.5 accent-blue-400 cursor-pointer" title="Mark as sick hours" data-testid={`shift-sick-${s.id}`} />
      </div>
      <div className="flex gap-1 items-center">
        {s.is_break_violation && <span className="w-2 h-2 rounded-full bg-orange-500" title={breakThreshold ? `Break >${breakThreshold}h` : "Break violation"} />}
        {s.is_short_break && <span className="w-2 h-2 rounded-full bg-red-400" title="Short break" />}
        {s.is_weekly_ot && <span className="w-2 h-2 rounded-full bg-purple-400" title="Weekly OT" />}
        {s.overtime != null && s.overtime > 0 && !s.is_weekly_ot && <span className="w-2 h-2 rounded-full bg-blue-400" title="Daily OT" />}
        {s.is_anomaly && <span className="w-2 h-2 rounded-full bg-yellow-400" title="Anomaly" />}
        {s.is_accidental && <span className="w-2 h-2 rounded-full bg-[#475569]" title="Accidental" />}
        {s.is_sick && <span className="w-2 h-2 rounded-full bg-sky-400" title="Sick" />}
      </div>
      <div>
        <input type="text" value={s.notes} onChange={(e) => onUpdate(s.id, "notes", e.target.value)} placeholder="Add note..." className="w-full text-[12px] bg-transparent border border-transparent rounded px-1 py-0.5 text-slate-600 placeholder-slate-400 focus:border-[#0A1F3B] focus:outline-none focus:text-[#0A1F3B] hover:border-slate-200" data-testid={`shift-notes-${s.id}`} />
      </div>
    </div>
  );
}

function MiniStat({ icon, label, value, variant = "" }) {
  const variantClasses = {
    warning: "bg-orange-500/10 text-orange-700",
    danger: "bg-red-500/10 text-red-700",
    purple: "bg-purple-500/10 text-purple-700",
    sick: "bg-blue-500/10 text-blue-700",
    muted: "bg-slate-50 text-slate-500",
  };
  return (
    <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded text-[12px] ${variantClasses[variant] || "bg-slate-50 text-slate-600"}`}>
      {icon}
      <span className="font-mono font-semibold text-[11px]">{value}</span>
      <span>{label}</span>
    </div>
  );
}

function getEmployeeFlags(shifts, breakThreshold) {
  const flags = [];
  const included = shifts.filter((s) => s.included);
  if (included.some((s) => s.is_anomaly)) flags.push({ label: "Anomaly", className: "bg-yellow-500/10 text-yellow-700 border-yellow-500/20", variant: "outline" });
  if (included.some((s) => s.is_break_violation)) flags.push({ label: breakThreshold ? `>${breakThreshold}h` : "Break", className: "bg-orange-500/10 text-orange-700 border-orange-500/20", variant: "outline" });
  if (included.some((s) => s.is_short_break)) flags.push({ label: "<30min", className: "bg-red-500/10 text-red-700 border-red-500/20", variant: "outline" });
  if (included.some((s) => s.is_weekly_ot)) flags.push({ label: "WkOT", className: "bg-purple-500/10 text-purple-700 border-purple-500/20", variant: "outline" });
  if (included.some((s) => s.overtime != null && s.overtime > 0 && !s.is_weekly_ot)) flags.push({ label: "DayOT", className: "bg-blue-500/10 text-blue-700 border-blue-500/20", variant: "outline" });
  if (included.some((s) => s.is_sick)) flags.push({ label: "Sick", className: "bg-sky-500/10 text-sky-700 border-sky-500/20", variant: "outline" });
  return flags;
}
